import React, { useState } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Button,
  Typography,
  Alert,
  Box,
  Checkbox,
  FormControlLabel,
} from '@mui/material'
import styles from '../styles.module.css'
import { ISchedulePattern } from '../../../../../../../../app/entities/SchedulePattern'
import { DayAnalysisResult } from '../../../../../../../../app/types/technician-impact-analysis'
import { TechnicianImpactReason } from '../../../../../../global/types/check-impact'

interface ImpactPreviewModalProps {
  open: boolean
  onClose: () => void
  technicianName: string
  effectiveDate: string
  analysisResults: DayAnalysisResult[]
  currentSchedulePattern?: ISchedulePattern // The current/old schedule pattern
  newSchedulePattern?: ISchedulePattern // The new schedule pattern being applied
  onConfirmOriginalDate: (overrideManualSettings: string[]) => void
  onUseSafeDate: (safeDate: string) => void // Now accepts the calculated safe date
  savingPattern?: boolean
}

export default function ImpactPreviewModal({
  open,
  onClose,
  technicianName,
  effectiveDate,
  analysisResults,
  currentSchedulePattern,
  newSchedulePattern,
  onConfirmOriginalDate,
  onUseSafeDate,
  savingPattern = false,
}: ImpactPreviewModalProps) {
  const [selectedManualOverrides, setSelectedManualOverrides] = useState<
    Set<string>
  >(new Set())

  // Categorize analysis results
  const autoResults = analysisResults.filter(
    (result) =>
      result.reason === TechnicianImpactReason.TIME_REDUCTION ||
      result.reason === TechnicianImpactReason.DAY_ELIMINATION,
  )
  const manualResults = analysisResults.filter(
    (result) =>
      result.reason === TechnicianImpactReason.MANUAL_OVERRIDE_CONFLICT ||
      result.reason === TechnicianImpactReason.MANUAL_OVERRIDE_INFO,
  )

  // Initialize manual overrides as unchecked by default (preserve manual settings)
  React.useEffect(() => {
    setSelectedManualOverrides(new Set())
  }, [manualResults.length])

  const handleConfirmOriginalDate = () => {
    onConfirmOriginalDate(Array.from(selectedManualOverrides))
  }

  const toggleManualOverride = (date: string) => {
    const newSelected = new Set(selectedManualOverrides)
    if (newSelected.has(date)) {
      newSelected.delete(date)
    } else {
      newSelected.add(date)
    }
    setSelectedManualOverrides(newSelected)
  }

  const formatDate = (dateString: string) => {
    // Parse as UTC to avoid timezone issues
    const date = new Date(dateString + 'T00:00:00.000Z')
    return date.toLocaleDateString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    })
  }

  const formatTime = (timeString: string) => {
    // Convert 24-hour format to 12-hour format
    const [hours, minutes] = timeString.split(':')
    const hour = parseInt(hours)
    const ampm = hour >= 12 ? 'pm' : 'am'
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour
    return `${displayHour}${minutes !== '00' ? ':' + minutes : ''}${ampm}`
  }

  // Calculate total jobs that will be rescheduled from auto-generated changes
  const getBaseJobCount = () => {
    return autoResults
      .filter((r) => !r.error && r.droppedOrderCount !== undefined)
      .reduce((sum, r) => sum + (r.droppedOrderCount || 0), 0)
  }

  // Calculate safe date: furthest auto-bumped date + 1 day
  const calculateSafeDate = () => {
    // Only consider auto-generated days that have dropped orders
    const autoDaysWithDroppedOrders = autoResults.filter(
      (r) =>
        !r.error &&
        r.droppedOrderCount !== undefined &&
        (r.droppedOrderCount || 0) > 0,
    )

    if (autoDaysWithDroppedOrders.length === 0) return null

    // Find the furthest future date
    const furthestDate = autoDaysWithDroppedOrders
      .map((r) => r.date) // YYYY-MM-DD format
      .sort((a, b) => a.localeCompare(b)) // Sort dates
      .pop() // Get the last (furthest) date

    if (!furthestDate) return null

    // Add 1 day to the furthest date
    const safeDate = new Date(furthestDate + 'T00:00:00.000Z')
    safeDate.setUTCDate(safeDate.getUTCDate() + 1)

    // Return as YYYY-MM-DD string
    return safeDate.toISOString().split('T')[0]
  }

  const safeDate = calculateSafeDate()
  const hasAutoBumpedJobs = getBaseJobCount() > 0

  const getManualOverrideJobCount = () => {
    return manualResults
      .filter(
        (r) =>
          selectedManualOverrides.has(r.date) &&
          !r.error &&
          r.droppedOrderCount !== undefined,
      )
      .reduce((sum, r) => sum + (r.droppedOrderCount || 0), 0)
  }

  const getTotalJobCount = () => {
    return getBaseJobCount() + getManualOverrideJobCount()
  }

  // Helper component to render clickable work order links
  const renderWorkOrderLinks = (
    orders: Array<{ workOrderId: number; objectId: string }>,
  ) => {
    if (!orders || orders.length === 0) return 'None'

    return orders.map((order, index) => (
      <span key={order.objectId}>
        <a
          href={`${process.env.REACT_APP_ORIGIN}/work-orders/${order.objectId}`}
          target='_blank'
          rel='noopener noreferrer'
          style={{
            color: 'var(--status-info-text)',
            textDecoration: 'underline',
            fontWeight: 500,
          }}
        >
          #{order.workOrderId}
        </a>
        {index < orders.length - 1 && ', '}
      </span>
    ))
  }

  // Helper function to check if a day has changed between patterns
  const isDayChanged = (weekNumber: number, dayName: string) => {
    if (
      !currentSchedulePattern?.weekPatterns ||
      !newSchedulePattern?.weekPatterns
    )
      return false

    const oldWeek = currentSchedulePattern.weekPatterns.find(
      (w) => w.week === weekNumber,
    )
    const newWeek = newSchedulePattern.weekPatterns.find(
      (w) => w.week === weekNumber,
    )

    const wasWorking = oldWeek?.days.includes(dayName) || false
    const willBeWorking = newWeek?.days.includes(dayName) || false

    return wasWorking !== willBeWorking
  }

  // Helper function to check if work times have changed
  const getWorkTimeChanges = () => {
    if (!currentSchedulePattern || !newSchedulePattern)
      return { hasChanges: false }

    const startChanged =
      currentSchedulePattern.workDayFrom !== newSchedulePattern.workDayFrom
    const endChanged =
      currentSchedulePattern.workDayTo !== newSchedulePattern.workDayTo
    const lunchChanged =
      currentSchedulePattern.lunchDuration !== newSchedulePattern.lunchDuration

    return {
      hasChanges: startChanged || endChanged || lunchChanged,
      startChanged,
      endChanged,
      lunchChanged,
    }
  }

  const workTimeChanges = getWorkTimeChanges()

  const renderSchedulePatternPreview = () => {
    if (!newSchedulePattern?.weekPatterns) return null

    return (
      <Box
        mb={3}
        p={3}
        sx={{
          backgroundColor: 'var(--bg-card-alt)',
          borderRadius: 2,
          border: '1px solid var(--border-light)',
        }}
      >
        <Typography
          variant='h6'
          gutterBottom
          sx={{ color: 'var(--text-primary)', fontWeight: 600 }}
        >
          Schedule Pattern Changes
        </Typography>

        {/* Current → New Layout */}
        <Box
          display='flex'
          gap={3}
          alignItems='center'
        >
          {/* Current Pattern (Left) */}
          {currentSchedulePattern?.weekPatterns && (
            <>
              <Box flex={1}>
                <Typography
                  variant='body2'
                  color='textSecondary'
                  gutterBottom
                  sx={{ fontWeight: 600 }}
                >
                  Current Weekly Schedule:
                </Typography>
                <Box
                  display='flex'
                  flexDirection='column'
                  gap={1}
                >
                  {[...currentSchedulePattern.weekPatterns]
                    .sort((a, b) => a.week - b.week)
                    .map((week) => (
                      <Box
                        key={week.week}
                        display='flex'
                        alignItems='center'
                        gap={1}
                        sx={{
                          padding: '8px 12px',
                          backgroundColor: 'var(--bg-card)',
                          borderRadius: 1,
                          border: '1px solid var(--border-medium)',
                        }}
                      >
                        <Typography
                          variant='body2'
                          sx={{
                            fontWeight: 600,
                            minWidth: '30px',
                            color: 'var(--color-muted)',
                          }}
                        >
                          W{week.week + 1}
                        </Typography>
                        {[
                          'sunday',
                          'monday',
                          'tuesday',
                          'wednesday',
                          'thursday',
                          'friday',
                          'saturday',
                        ].map((day) => {
                          const isWorkDay = week.days.includes(day)
                          return (
                            <Box
                              key={day}
                              style={{
                                width: 28,
                                height: 28,
                                backgroundColor: isWorkDay
                                  ? 'var(--status-success-text)'
                                  : 'var(--color-muted)',
                                borderRadius: '4px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'var(--text-inverse)',
                                fontSize: '11px',
                                fontWeight: 'bold',
                              }}
                            >
                              {day.charAt(0).toUpperCase()}
                            </Box>
                          )
                        })}
                      </Box>
                    ))}
                </Box>

                {/* Current Work Times */}
                <Box
                  mt={2}
                  p={2}
                  sx={{
                    backgroundColor: 'var(--bg-card)',
                    borderRadius: 1,
                    border: '1px solid var(--border-medium)',
                  }}
                >
                  <Typography
                    variant='body2'
                    color='textSecondary'
                    gutterBottom
                  >
                    Current Work Hours:
                  </Typography>
                  <Typography
                    variant='body1'
                    sx={{ fontWeight: 500 }}
                  >
                    {currentSchedulePattern.workDayFrom &&
                    currentSchedulePattern.workDayTo
                      ? `${formatTime(
                          currentSchedulePattern.workDayFrom,
                        )} - ${formatTime(currentSchedulePattern.workDayTo)}`
                      : 'Times to be determined'}
                  </Typography>
                  {currentSchedulePattern.lunchDuration && (
                    <Typography
                      variant='body2'
                      color='textSecondary'
                    >
                      Lunch: {currentSchedulePattern.lunchDuration} min
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Arrow */}
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  px: 2,
                }}
              >
                <Typography
                  variant='h4'
                  sx={{ color: 'var(--status-almost-late-accent)', fontWeight: 'bold' }}
                >
                  →
                </Typography>
                <Typography
                  variant='caption'
                  color='textSecondary'
                  sx={{ mt: 1, textAlign: 'center' }}
                >
                  Changes take effect:
                  <br />
                  {formatDate(effectiveDate)}
                </Typography>
              </Box>
            </>
          )}

          {/* New Pattern (Right) */}
          <Box flex={1}>
            <Typography
              variant='body2'
              color='textSecondary'
              gutterBottom
              sx={{ fontWeight: 600 }}
            >
              New Weekly Schedule:
            </Typography>
            <Box
              display='flex'
              flexDirection='column'
              gap={1}
            >
              {[...newSchedulePattern.weekPatterns]
                .sort((a, b) => a.week - b.week)
                .map((week) => (
                  <Box
                    key={week.week}
                    display='flex'
                    alignItems='center'
                    gap={1}
                    sx={{
                      padding: '8px 12px',
                      backgroundColor: 'var(--bg-card)',
                      borderRadius: 1,
                      border: '1px solid var(--border-medium)',
                    }}
                  >
                    <Typography
                      variant='body2'
                      sx={{
                        fontWeight: 600,
                        minWidth: '30px',
                        color: 'var(--color-muted)',
                      }}
                    >
                      W{week.week + 1}
                    </Typography>
                    {[
                      'sunday',
                      'monday',
                      'tuesday',
                      'wednesday',
                      'thursday',
                      'friday',
                      'saturday',
                    ].map((day) => {
                      const hasChanged = isDayChanged(week.week, day)
                      const isWorkDay = week.days.includes(day)
                      return (
                        <Box
                          key={day}
                          style={{
                            width: 28,
                            height: 28,
                            backgroundColor: isWorkDay
                              ? 'var(--status-success-text)'
                              : 'var(--color-muted)',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'var(--text-inverse)',
                            fontSize: '11px',
                            fontWeight: 'bold',
                            border: hasChanged ? '2px solid var(--status-warning-accent-light)' : 'none',
                            boxSizing: 'border-box',
                          }}
                        >
                          {day.charAt(0).toUpperCase()}
                        </Box>
                      )
                    })}
                  </Box>
                ))}
            </Box>

            {/* New Work Times */}
            <Box
              mt={2}
              p={2}
              sx={{
                backgroundColor: 'var(--bg-card)',
                borderRadius: 1,
                border: workTimeChanges.hasChanges
                  ? '2px solid var(--status-warning-accent-light)'
                  : '1px solid var(--border-medium)',
              }}
            >
              <Typography
                variant='body2'
                color='textSecondary'
                gutterBottom
              >
                New Work Hours:
              </Typography>
              <Typography
                variant='body1'
                sx={{ fontWeight: 500 }}
              >
                {newSchedulePattern.workDayFrom && newSchedulePattern.workDayTo
                  ? `${formatTime(
                      newSchedulePattern.workDayFrom,
                    )} - ${formatTime(newSchedulePattern.workDayTo)}`
                  : 'Times to be determined'}
              </Typography>
              {newSchedulePattern.lunchDuration && (
                <Typography
                  variant='body2'
                  color='textSecondary'
                >
                  Lunch: {newSchedulePattern.lunchDuration} min
                </Typography>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='lg'
      fullWidth
      PaperProps={{
        className: styles.modalPaper,
      }}
    >
      <DialogTitle className={styles.modalHeader}>
        <div className={styles.headerContent}>
          <div className={styles.modalTitle}>
            Schedule Change Impact - {technicianName}
          </div>
        </div>
      </DialogTitle>

      <DialogContent
        className={styles.modalContent}
        sx={{
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        {/* New Schedule Pattern Preview */}
        {renderSchedulePatternPreview()}

        {/* Side-by-side layout with proper borders */}
        <Box
          display='flex'
          gap={3}
        >
          {/* LEFT SIDE: Jobs to be Rescheduled */}
          <Box
            flex={1}
            sx={{
              border:
                getBaseJobCount() > 0
                  ? '1px solid var(--status-warning-accent-light)'
                  : '1px solid var(--border-medium)',
              borderRadius: 2,
              backgroundColor: 'var(--bg-card)',
            }}
          >
            <Box
              p={2}
              sx={{
                borderBottom: '1px solid var(--border-medium)',
                backgroundColor: getBaseJobCount() > 0 ? 'var(--status-warning-bg)' : 'var(--bg-card-alt)',
              }}
            >
              <Typography
                variant='h6'
                sx={{ fontWeight: 600, color: 'var(--text-primary)' }}
              >
                Jobs to be Rescheduled ({getBaseJobCount()})
              </Typography>
              {getBaseJobCount() > 0 && (
                <Typography
                  variant='caption'
                  color='warning.main'
                  display='block'
                  sx={{ mt: 0.5, fontWeight: 500 }}
                >
                  ⚠️ These jobs will be bumped to pending rescheduling and an
                  email will be sent to mobileinstall@tirerack.com to get them
                  rescheduled
                </Typography>
              )}
            </Box>

            <Box p={2}>
              {autoResults.length === 0 || getBaseJobCount() === 0 ? (
                <Box
                  p={3}
                  textAlign='center'
                  sx={{
                    backgroundColor: 'var(--status-success-bg-light)',
                    borderRadius: 1,
                    border: '1px solid var(--status-success-border)',
                    color: 'var(--status-success-text-dark)',
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{ fontWeight: 500 }}
                  >
                    ✅ No jobs will be affected
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {autoResults.map((result, index) => (
                    <Box
                      key={result.date}
                      sx={{
                        py: 1.5,
                        borderBottom:
                          index < autoResults.length - 1
                            ? '1px solid var(--hover-bg-neutral)'
                            : 'none',
                      }}
                    >
                      <Typography
                        variant='subtitle2'
                        gutterBottom
                        sx={{ fontWeight: 600 }}
                      >
                        {formatDate(result.date)}
                      </Typography>

                      {result.error ? (
                        <Alert
                          severity='error'
                          sx={{ mt: 1 }}
                        >
                          Analysis failed: {result.error}
                        </Alert>
                      ) : (
                        <Typography
                          variant='body2'
                          color='textSecondary'
                        >
                          {result.droppedOrderCount || 0} job
                          {(result.droppedOrderCount || 0) !== 1
                            ? 's'
                            : ''}:{' '}
                          {renderWorkOrderLinks(result.droppedOrders || [])}
                        </Typography>
                      )}
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>

          {/* RIGHT SIDE: Override Custom Days */}
          <Box
            flex={1}
            sx={{
              border: '1px solid var(--border-medium)',
              borderRadius: 2,
              backgroundColor: 'var(--bg-card)',
            }}
          >
            <Box
              p={2}
              sx={{
                borderBottom: '1px solid var(--border-medium)',
                backgroundColor: 'var(--bg-card-alt)',
              }}
            >
              <Typography
                variant='h6'
                sx={{ fontWeight: 600, color: 'var(--text-primary)' }}
              >
                Override Manual Schedule Edits?
              </Typography>
              <Typography
                variant='caption'
                color='textSecondary'
                display='block'
                sx={{ mt: 0.5 }}
              >
                Check any that you would like to override with the new pattern,
                or leave unchecked to keep their current settings.
              </Typography>
            </Box>

            <Box p={2}>
              {manualResults.length === 0 ? (
                <Box
                  p={3}
                  textAlign='center'
                  sx={{
                    backgroundColor: 'var(--bg-card-alt)',
                    borderRadius: 1,
                    border: '1px solid var(--border-medium)',
                    color: 'var(--color-muted)',
                  }}
                >
                  <Typography variant='body2'>
                    No manual day settings to override
                  </Typography>
                </Box>
              ) : (
                <Box>
                  {manualResults.map((result, index) => (
                    <Box
                      key={result.date}
                      sx={{
                        py: 1,
                        px: 1,
                        borderRadius: '8px',
                        marginBottom: 1,
                        backgroundColor: selectedManualOverrides.has(
                          result.date,
                        )
                          ? result.reason ===
                            TechnicianImpactReason.MANUAL_OVERRIDE_INFO
                            ? 'var(--hover-bg-success)' // Light green for safe overrides
                            : 'var(--status-warning-bg)' // Light orange for conflicts
                          : 'transparent',
                        border: selectedManualOverrides.has(result.date)
                          ? result.reason ===
                            TechnicianImpactReason.MANUAL_OVERRIDE_INFO
                            ? '1px solid var(--status-success-border)'
                            : '1px solid var(--status-warning-accent-light)'
                          : 'none',
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={selectedManualOverrides.has(result.date)}
                            onChange={() => toggleManualOverride(result.date)}
                            size='small'
                          />
                        }
                        label={
                          <Box
                            sx={{
                              display: 'grid',
                              gridTemplateColumns: '12rem 1fr',
                              gap: 2,
                              width: '100%',
                              alignItems: 'center', // Changed from flex-start to center
                            }}
                          >
                            {/* Left column: Date, current settings, reason - all in colored box */}
                            <Box
                              sx={{
                                backgroundColor: result.manualDaySetting
                                  ?.workDay
                                  ? 'var(--status-success-text)' // Green for working days
                                  : 'var(--color-muted)', // Gray for non-working days
                                color: 'var(--text-inverse)',
                                padding: '8px 12px',
                                borderRadius: '6px',
                              }}
                            >
                              <Typography
                                variant='subtitle2'
                                sx={{ fontWeight: 600 }}
                              >
                                {formatDate(result.date)}
                              </Typography>
                              <Typography
                                variant='caption'
                                display='block'
                                sx={{ fontWeight: 'bold', mt: 0.5 }}
                              >
                                Current:{' '}
                                {result.manualDaySetting?.workDay
                                  ? `${formatTime(
                                      result.manualDaySetting.startTime ||
                                        '09:00',
                                    )}-${formatTime(
                                      result.manualDaySetting.endTime ||
                                        '17:00',
                                    )}`
                                  : 'Off day'}
                              </Typography>
                              {result.manualDaySetting?.reason && (
                                <Typography
                                  variant='caption'
                                  display='block'
                                  sx={{ opacity: 0.9, mt: 0.5 }}
                                >
                                  Reason: "{result.manualDaySetting.reason}"
                                </Typography>
                              )}
                            </Box>

                            {/* Right column: Impact status - centered and more prominent */}
                            <Box
                              sx={{
                                textAlign: 'center',
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                              }}
                            >
                              {!result.error &&
                                result.droppedOrderCount !== undefined && (
                                  <>
                                    <Box
                                      sx={{
                                        backgroundColor:
                                          result.reason ===
                                          TechnicianImpactReason.MANUAL_OVERRIDE_INFO
                                            ? 'var(--status-success-bg-light)' // Light green background
                                            : 'var(--status-warning-bg-light)', // Light yellow background
                                        color:
                                          result.reason ===
                                          TechnicianImpactReason.MANUAL_OVERRIDE_INFO
                                            ? 'var(--status-success-text-dark)' // Dark green text
                                            : 'var(--status-warning-text-dark)', // Dark yellow text
                                        padding: '6px 12px',
                                        borderRadius: '20px',
                                        border: `1px solid ${
                                          result.reason ===
                                          TechnicianImpactReason.MANUAL_OVERRIDE_INFO
                                            ? 'var(--status-success-border)'
                                            : 'var(--status-warning-border)'
                                        }`,
                                        display: 'inline-block',
                                        fontWeight: 600,
                                        fontSize: '0.75rem',
                                        textAlign: 'center',
                                      }}
                                    >
                                      {result.reason ===
                                      TechnicianImpactReason.MANUAL_OVERRIDE_INFO
                                        ? selectedManualOverrides.has(
                                            result.date,
                                          )
                                          ? '✓ No conflict'
                                          : '✓ No conflict if overridden'
                                        : selectedManualOverrides.has(
                                            result.date,
                                          )
                                        ? `⚠️ ${result.droppedOrderCount} job${
                                            result.droppedOrderCount !== 1
                                              ? 's'
                                              : ''
                                          } will be bumped`
                                        : `⚠️ ${result.droppedOrderCount} job${
                                            result.droppedOrderCount !== 1
                                              ? 's'
                                              : ''
                                          } bumped if overridden`}
                                    </Box>
                                    {result.reason !==
                                      TechnicianImpactReason.MANUAL_OVERRIDE_INFO &&
                                      result.droppedOrders &&
                                      result.droppedOrders.length > 0 && (
                                        <Typography
                                          variant='caption'
                                          color='textSecondary'
                                          display='block'
                                          sx={{ mt: 1, textAlign: 'center' }}
                                        >
                                          {renderWorkOrderLinks(
                                            result.droppedOrders,
                                          )}
                                        </Typography>
                                      )}
                                  </>
                                )}
                            </Box>
                          </Box>
                        }
                        sx={{ alignItems: 'center', mt: 0.5 }} // Changed to center alignment
                      />
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          </Box>
        </Box>
      </DialogContent>

      {/* STICKY SUMMARY WITH ACTIONS (always visible) */}
      <Box
        p={3}
        sx={{
          backgroundColor: getTotalJobCount() > 0 ? 'var(--status-warning-bg)' : 'var(--hover-bg-success)',
          borderTop: '1px solid var(--border-medium)',
          borderRadius: 0,
        }}
      >
        {/* Side-by-side layout: Summary on left, Actions on right */}
        <Box
          display='flex'
          alignItems='center'
          justifyContent='space-between'
        >
          {/* Left side: Summary */}
          <Box flex={1}>
            <Typography
              variant='h6'
              sx={{
                color: getTotalJobCount() > 0 ? 'var(--status-warning-text)' : 'var(--status-success-text)',
                fontWeight: 700,
                display: 'flex',
                alignItems: 'center',
                gap: 1.5,
              }}
            >
              {getTotalJobCount() > 0 ? '⚠️' : '✅'}
              <span>
                {getTotalJobCount() === 0
                  ? 'No jobs will be affected'
                  : 'Jobs will be bumped'}
              </span>
            </Typography>
          </Box>

          {/* Right side: Action buttons */}
          <Box
            display='flex'
            gap={2}
          >
            <Button
              onClick={onClose}
              color='inherit'
              variant='outlined'
            >
              Cancel
            </Button>

            {hasAutoBumpedJobs && safeDate && (
              <Button
                onClick={() => onUseSafeDate(safeDate)}
                color='primary'
                variant='outlined'
              >
                Move starting date to {formatDate(safeDate)} to avoid bumping
                jobs
              </Button>
            )}

            <Button
              onClick={handleConfirmOriginalDate}
              color={getTotalJobCount() > 0 ? 'warning' : 'primary'}
              variant='contained'
              sx={{ fontWeight: 600 }}
              disabled={savingPattern}
            >
              {savingPattern
                ? 'Saving...'
                : getTotalJobCount() > 0
                ? `Update Schedule and Bump ${getTotalJobCount()} Job${
                    getTotalJobCount() !== 1 ? 's' : ''
                  }`
                : 'Update Schedule'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Dialog>
  )
}
